import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Row } from "reactstrap";
import {
  getFormattedContactNumber,
  getFormattedOptions,
  getModifiedObject,
  Toast,
  uploadImage,
} from "utility/helpers";
import { fetchAllRegions } from "services/services.congressman";
import { fetchPartylists } from "services/services.affiliation";
import { genericPost, genericPut } from "services/services.general";
import { fetchCandidates } from "services/services.official";
import ImgContainer from "components/common/img";
import defaultUserImage from "assets/img/default_user.png";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import {
  FormContainer,
  FormHeaderSection,
  FormSection,
  HeaderText,
  ImageWrapper,
  UploadButton,
  UploadInput,
} from "assets/js/Styles";
import DirectoryForm from "../Form";
import { POSITION_MAYOR } from "../constants";
import { validateOfficialDirectoryForm } from "../senator/utility";

const EditMayor = () => {
  const location = useLocation();
  const mayor = location.state?.mayor || null;

  const [isLoading, setIsLoading] = useState(false);
  const [partylists, setPartylists] = useState([]);
  const [selectedPartylist, setSelectedPartylist] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const [regionOptions, setRegionOptions] = useState([]);

  const [mayorOptions, setMayorOptions] = useState([]);
  const [selectedMayor, setSelectedMayor] = useState(null);

  const [selectedRegion, setSelectedRegion] = useState(null);

  const [formFields, setFormFields] = useState({
    lastName: "",
    firstName: "",
    fullName: "",
    omegaId: "",
    region: "",
    contact: "",
    province: "",
    municipality: "",
    party: "",
    cos: "",
    cosNumber: "",
    photo: "",
    photoName: "",
    cosFirstName: "",
    cosLastName: "",
    name: "",
  });

  const navigate = useNavigate();

  const fetchMayors = async () => {
    await fetchCandidates({
      url: "/election/nominations/?position_id=5&is_winner=true&page_size=1700&is_active=true",
      callback: (data) => {
        if (data.results) {
          const rows = data.results;

          const mayors = rows.map((myr) => ({
            ...myr,
            value: myr.candidate.id,
            label: `${myr.candidate.first_name} ${myr.candidate.last_name}`,
          }));

          if (mayor) {
            const selectedMyr = mayors.find(
              (myr) => myr.candidate.id === mayor.omegaId
            );
            setSelectedMayor(selectedMyr);
          }

          setMayorOptions(mayors);
        }
      },
    });
  };

  const fetchRegion = () => {
    fetchAllRegions({
      callback: (data) => {
        if (data.length) {
          const options = data
            .filter((region) => region.name !== "Partylist")
            .map(({ acronym, id, omegaId }) => ({
              omegaId,
              label: acronym,
              value: id,
            }));

          setRegionOptions(options);

          const region = options?.find(
            (option) => option.value === mayor?.regionId
          );

          if (region) {
            setSelectedRegion(region);
          } else {
            setFormFields({
              ...formFields,
              region: null,
            });
          }
        }
      },
    });
  };

  const setData = () => {
    const option = partylists.find(
      (partylist) => partylist.id === mayor.partyId
    );

    if (option) setSelectedPartylist(option);

    setIsLoading(false);
  };

  const setMayorDetails = () => {
    const updatedFields = {
      ...formFields,
      lastName: mayor.lastName,
      firstName: mayor.firstName,
      fullName: mayor.fullName,
      contact: getFormattedContactNumber(mayor.contact),
      party: mayor.partyId,
      region: selectedRegion,
      provinceId: mayor.provinceId,
      municipalityId: mayor.municipalityId,
      cos: mayor.cosName,
      cosNumber: getFormattedContactNumber(mayor.cosContact),
      photo: mayor.photo || "",
      cosFirstName: mayor.cosFirstName || mayor.cosName,
      cosLastName: mayor.cosLastName,
    };

    if (mayor.photo) {
      const imgContainer = document.querySelector(".mayor-img");
      imgContainer.src = mayor.photo;
    }

    setFormFields(updatedFields);
  };

  const sendData = (formData) => {
    setIsLoading(true);

    if (mayor) {
      genericPut({
        url: `/official/${mayor.id}`,
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_MAYOR },
            });

            Toast({
              type: 1,
              content: "Successfully edited mayor details",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    } else {
      genericPost({
        url: "/official",
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_MAYOR },
            });

            Toast({
              type: 1,
              content: "Successfully added mayor.",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    }
  };

  const handleOnSubmit = async () => {
    const isValid = await validateOfficialDirectoryForm(formFields);

    if (isValid.valid) {
      const data = {
        // eslint-disable-next-line max-len
        fullName: `MAYOR ${formFields.firstName} ${formFields.lastName}`,
        firstName: formFields.firstName,
        lastName: formFields.lastName,
        contact: formFields.contact?.slice(1),
        regionId: formFields.region.value,
        provinceId: formFields.province.value,
        municipalityId:
          typeof formFields.municipality.value === "number"
            ? formFields.municipality.value
            : null,
        cosName: `${formFields.cosFirstName} ${formFields.cosLastName}`,
        cosFirstName: formFields.cosFirstName,
        cosLastName: formFields.cosLastName,
        omegaId: formFields.omegaId,
        position: POSITION_MAYOR,
        ...(formFields.cosNumber ? { cosContact: formFields.cosNumber } : {}),
        ...(selectedPartylist ? { partyId: selectedPartylist.value } : {}),
      };

      if (formFields.cosNumber) {
        data.cosContact = formFields.cosNumber?.slice(1);
      }

      let initialData = {};

      if (mayor) {
        initialData = {
          firstName: mayor.firstName,
          lastName: mayor.lastName,
          fullName: mayor.fullName,
          contact: mayor.contact,
          regionId: mayor.regionId,
          provinceId: mayor.provinceId,
          municipalityId: mayor.municipalityId,
          partyId: mayor.partyId,
          cosName: mayor.cosName,
          cosContact: mayor.cosContact,
          cosFirstName: mayor.cosFirstName,
          cosLastName: mayor.cosLastName,
          photo: mayor.photo,
          omegaId: formFields.omegaId,
          position: POSITION_MAYOR,
        };
      }

      setIsLoading(true);

      if (typeof formFields.photo !== "string") {
        const fileName = await uploadImage({
          photo: formFields.photo,
          photoName: formFields.photoName,
        });
        data.photo = fileName;
      }

      const formData = mayor
        ? await getModifiedObject(initialData, data)
        : data;

      await sendData(formData);
    } else {
      Toast({
        type: 4,
        content: isValid.error,
      });
    }
  };

  const fetchData = () => {
    fetchPartylists({
      callback: (data) => {
        const newData = getFormattedOptions("name", data);

        setPartylists(newData);
      },
    });

    setIsLoading(false);
  };

  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".mayor-img");

        const fileName = mayor
          ? `${
              mayor.id
            }-${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`
          : `${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`;

        setFormFields({
          ...formFields,
          photo: file,
          photoName: fileName,
        });

        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    //  fetch all district, partylist, region
    fetchRegion();
    fetchData();
    fetchMayors();
  }, []);

  useEffect(() => {
    if (mayor !== null) {
      setData();
      setMayorDetails();
      setIsEdit(true);
    }
  }, [partylists, mayor]);

  return (
    <FormContainer>
      <Col className="container">
        <FormHeaderSection>
          <HeaderText>{!mayor ? "Add Mayor" : "Mayor Details"}</HeaderText>
        </FormHeaderSection>

        <FormSection>
          <Row>
            <Col
              xs="12"
              sm="6"
              lg="4"
            >
              <ImageWrapper>
                <ImgContainer
                  alt="mayor-img"
                  className="mayor-img"
                  src={defaultUserImage}
                  width="196px"
                  height="196px"
                />

                <UploadButton htmlFor="file-upload">
                  {"Upload Photo"}
                  <UploadInput
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                </UploadButton>
              </ImageWrapper>
            </Col>
            <Col
              xs="12"
              sm="6"
              lg="8"
            >
              <DirectoryForm
                handleOnSubmit={handleOnSubmit}
                formFields={formFields}
                setFormFields={setFormFields}
                governorOptions={mayorOptions}
                selectedGovernor={selectedMayor}
                setSelectedGovernor={setSelectedMayor}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                regionOptions={regionOptions}
                partylists={partylists}
                selectedPartylist={selectedPartylist}
                setSelectedPartylist={setSelectedPartylist}
                isLoading={isLoading}
                isEdit={isEdit}
                position={POSITION_MAYOR}
              />
            </Col>
          </Row>
        </FormSection>
      </Col>
    </FormContainer>
  );
};

export default EditMayor;
